import axios from "axios";
import { Base_URL } from "../url";

export const saveEventDetails = async (eventData) => {
  try {
    const response = await axios.post(
      `${Base_URL}/EventDetails/SaveEvents`,
      eventData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error.response?.data || "Failed to save event";
  }
};

export const updateEvent = async (eventCode, data) => {
  const res = await axios.put(
    `${Base_URL}/EventDetails/UpdateEvent/${eventCode}`,
    data,
  );
  return res.data;
};

export const addParticipants = async (eventCode, data) => {
  const res = await axios.post(
    `${Base_URL}/EventDetails/AddParticipants/${eventCode}`,
    data,
  );
  return res.data;
};

export const addReminder = async (eventCode, data) => {
  const res = await axios.post(
    `${Base_URL}/EventDetails/AddReminder/${eventCode}`,
    data,
  );
  return res.data;
};

export const addMoM = async (eventCode, data) => {
  const res = await axios.post(
    `${Base_URL}/EventDetails/AddMoM/${eventCode}`,
    data,
  );
  return res.data;
};

export const getAllEventTypes = async () => {
  try {
    const res = await axios.get(`${Base_URL}/EventDetails/GetAllEventTypes`);
    return res.data;
  } catch (error) {
    throw error.response?.data || "Failed to fetch event types";
  }
};

export const getEventTypeByGuid = async (guid) => {
  try {
    const res = await axios.get(
      `${Base_URL}/EventDetails/GetEventTypeByGuid/${guid}`,
    );
    return res.data;
  } catch (error) {
    throw error.response?.data || "Failed to fetch event type";
  }
};

// Add to Api/Event.js

// ================= PARTICIPANT CRUD =================

export const updateParticipant = async (participantGuid, data) => {
  const res = await axios.put(
    `${Base_URL}/EventDetails/UpdateParticipant/${participantGuid}`,
    data,
  );
  return res.data;
};

export const deleteParticipant = async (participantGuid, deletedBy) => {
  const res = await axios.delete(
    `${Base_URL}/EventDetails/DeleteParticipant/${participantGuid}`,
    { params: { deletedBy } },
  );
  return res.data;
};

// ================= REMINDER CRUD =================

export const updateReminder = async (reminderGuid, data) => {
  const res = await axios.put(
    `${Base_URL}/EventDetails/UpdateReminder/${reminderGuid}`,
    data,
  );
  return res.data;
};

export const deleteReminder = async (reminderGuid, deletedBy) => {
  const res = await axios.delete(
    `${Base_URL}/EventDetails/DeleteReminder/${reminderGuid}`,
    { params: { deletedBy } },
  );
  return res.data;
};

// ================= MOM CRUD =================

export const updateMoMHeader = async (eventCode, data) => {
  const res = await axios.put(
    `${Base_URL}/EventDetails/UpdateMoMHeader/${eventCode}`,
    data,
  );
  return res.data;
};

export const updateMoMDetail = async (detailGuid, data) => {
  const res = await axios.put(
    `${Base_URL}/EventDetails/UpdateMoMDetail/${detailGuid}`,
    data,
  );
  return res.data;
};

export const deleteMoMDetail = async (detailGuid, deletedBy) => {
  const res = await axios.delete(
    `${Base_URL}/EventDetails/DeleteMoMDetail/${detailGuid}`,
    { params: { deletedBy } },
  );
  return res.data;
};

export const addMoMDetail = async (eventCode, data) => {
  const res = await axios.post(
    `${Base_URL}/EventDetails/AddMoMDetail/${eventCode}`,
    data,
  );
  return res.data;
};

// ================= EVENT DOCUMENT APIS =================

/**
 * Upload a single event document
 * @param {Object} params - Upload parameters
 * @param {File} params.file - The file to upload
 * @param {number} params.eventDoctypeId - Document type ID from frontend
 * @param {string} params.eventParentId - EventCode or EventId
 * @param {number} params.orgId - Organization ID
 * @param {string} params.createdBy - Username of creator (optional)
 * @returns {Promise} Upload response
 */
export const uploadEventDocument = async ({
  file,
  eventDoctypeId,
  eventParentId,
  orgId,
  createdBy,
}) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("eventDoctypeId", eventDoctypeId);
    formData.append("eventParentId", eventParentId);
    formData.append("orgId", orgId);
    if (createdBy) {
      formData.append("createdBy", createdBy);
    }

    const response = await axios.post(
      `${Base_URL}/EventDetails/UploadEventDocument`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error.response?.data || "Error uploading document!";
  }
};

/**
 * Upload multiple event documents
 * @param {Object} params - Upload parameters
 * @param {File[]} params.files - Array of files to upload
 * @param {number} params.eventDoctypeId - Document type ID from frontend
 * @param {string} params.eventParentId - EventCode or EventId
 * @param {number} params.orgId - Organization ID
 * @param {string} params.createdBy - Username of creator (optional)
 * @returns {Promise} Upload response
 */
export const uploadMultipleEventDocuments = async ({
  files,
  eventDoctypeId,
  eventParentId,
  orgId,
  createdBy,
}) => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("eventDoctypeId", eventDoctypeId);
    formData.append("eventParentId", eventParentId);
    formData.append("orgId", orgId);
    if (createdBy) {
      formData.append("createdBy", createdBy);
    }

    const response = await axios.post(
      `${Base_URL}/EventDetails/UploadMultipleEventDocuments`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error.response?.data || "Error uploading documents!";
  }
};

/**
 * Get all documents for an event
 * @param {number} orgId - Organization ID
 * @param {string} eventCode - Event code
 * @returns {Promise} List of documents
 */
export const getEventDocuments = async (orgId, eventCode) => {
  try {
    const response = await axios.get(
      `${Base_URL}/EventDetails/GetEventDocuments/${orgId}/${eventCode}`,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error fetching documents!";
  }
};

/**
 * Download a specific document
 * @param {string} documentGuid - Document GUID
 * @returns {Promise} Blob response for download
 */
export const downloadEventDocument = async (documentGuid) => {
  try {
    const response = await axios.get(
      `${Base_URL}/EventDetails/DownloadEventDocument/${documentGuid}`,
      {
        responseType: "blob",
      },
    );
    return response;
  } catch (error) {
    throw error.response?.data || "Error downloading document!";
  }
};

/**
 * Delete (soft delete) a document
 * @param {string} documentGuid - Document GUID
 * @param {string} deletedBy - Username of person deleting (optional)
 * @returns {Promise} Delete response
 */
export const deleteEventDocument = async (documentGuid, deletedBy = null) => {
  try {
    const params = deletedBy ? { deletedBy } : {};
    const response = await axios.delete(
      `${Base_URL}/EventDetails/DeleteEventDocument/${documentGuid}`,
      {
        params: params,
      },
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || "Error deleting document!";
  }
};

// Helper function to trigger file download
export const downloadFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
